import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import { useFavorites } from "../auth/FavoritesContext";
import KidsShell from "../components/KidsShell";
import { buildSeriesShows, showToCatalogItem } from "../series";

function KidsWatchLink({ to, className, children, ...props }) {
  const location = useLocation();
  return (
    <Link to={to} state={{ background: location }} className={className} {...props}>
      {children}
    </Link>
  );
}

function KidsPoster({ video }) {
  const posterSrc =
    video.cover_url ||
    video.poster_url ||
    video.series_poster_url ||
    video.backdrop_url ||
    "";

  return (
    <KidsWatchLink
      to={video.catalog_href || `/watch/${video.id}`}
      className="kids-poster"
      draggable={false}
      title={video.title}
    >
      <div className="kids-poster-media">
        {posterSrc ? (
          <img src={posterSrc} alt={video.title} loading="lazy" decoding="async" />
        ) : (
          <div className="kids-poster-fallback">
            {(video.title || "?").slice(0, 1)}
          </div>
        )}
      </div>
      <span className="kids-poster-title">{video.title}</span>
    </KidsWatchLink>
  );
}

function KidsGridSection({ title, items, linkTo, emptyText, accent = "indigo", subtitle, hideHead = false }) {
  if (!items.length) {
    if (!emptyText) return null;
    return (
      <section className={`kids-section kids-section-${accent}`}>
        {!hideHead && title ? (
          <div className="kids-section-head">
            <h2 className="kids-section-title">
              <span className="kids-section-badge">{title}</span>
              {subtitle ? <span className="kids-section-sub">{subtitle}</span> : null}
            </h2>
          </div>
        ) : null}
        <p className="kids-empty">{emptyText}</p>
      </section>
    );
  }

  const showHead = !hideHead && (title || subtitle || linkTo);

  return (
    <section className={`kids-section kids-section-${accent}`}>
      {showHead ? (
        <div className="kids-section-head">
          {title || subtitle ? (
            <h2 className="kids-section-title">
              {title ? <span className="kids-section-badge">{title}</span> : null}
              {subtitle ? <span className="kids-section-sub">{subtitle}</span> : null}
            </h2>
          ) : (
            <span />
          )}
          {linkTo ? (
            <Link to={linkTo} className="kids-section-more">
              Ver más
              <span className="kids-section-more-arrow" aria-hidden="true">
                →
              </span>
            </Link>
          ) : null}
        </div>
      ) : null}
      <div className="kids-poster-grid">
        {items.map((video) => (
          <KidsPoster key={video.catalog_key || video.id} video={video} />
        ))}
      </div>
    </section>
  );
}

function KidsFeaturedCarousel({ items, profileName }) {
  const [index, setIndex] = useState(0);
  const count = items.length;
  const current = items[index] || null;

  useEffect(() => {
    setIndex(0);
  }, [items]);

  useEffect(() => {
    if (count < 2) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % count);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [count, index]);

  if (!current) return null;

  function go(next) {
    if (!count) return;
    setIndex(((next % count) + count) % count);
  }

  const art =
    current.backdrop_url || current.cover_url || current.series_backdrop_url;

  return (
    <section
      className="kids-hero-billboard"
      style={art ? { "--kids-hero-image": `url(${art})` } : undefined}
    >
      <div
        className="kids-hero-billboard-bg"
        key={current.catalog_key || current.id}
        aria-hidden="true"
      />
      <div className="kids-hero-billboard-fade" aria-hidden="true" />
      <div className="kids-hero-billboard-content">
        <p className="kids-hero-kicker">
          Casasm Kids · Hey, {profileName || "tú"}
          {current.category ? ` · ${current.category}` : ""}
        </p>
        <h1>{current.title}</h1>
        <p className="kids-hero-synopsis">
          Listo para reproducir en tu zona infantil.
        </p>
        <div className="kids-hero-actions">
          <KidsWatchLink
            to={current.playHref || current.catalog_href || `/watch/${current.id}`}
            className="kids-btn kids-btn-play"
          >
            Reproducir
          </KidsWatchLink>
          <Link to="/kids/tv" className="kids-btn kids-btn-secondary">
            TV en vivo
          </Link>
        </div>
      </div>
      {count > 1 ? (
        <>
          <button
            type="button"
            className="kids-hero-nav kids-hero-nav-left"
            aria-label="Anterior destacado"
            onClick={() => go(index - 1)}
          >
            ‹
          </button>
          <button
            type="button"
            className="kids-hero-nav kids-hero-nav-right"
            aria-label="Siguiente destacado"
            onClick={() => go(index + 1)}
          >
            ›
          </button>
        </>
      ) : null}
    </section>
  );
}

function filterByQuery(items, query) {
  const q = (query || "").trim().toLowerCase();
  if (q.length < 2) return items;
  return items.filter((item) =>
    String(item.title || "")
      .toLowerCase()
      .includes(q)
  );
}

function useKidsCatalog() {
  const { token, profile } = useAuth();
  const { favoriteIds } = useFavorites();
  const [videos, setVideos] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api("/api/videos", { token })
      .then((data) => {
        if (!cancelled) setVideos(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, profile?.id]);

  const movies = useMemo(
    () => videos.filter((v) => (v.content_type || "movie") === "movie"),
    [videos]
  );

  const animeMovies = useMemo(
    () => videos.filter((v) => v.content_type === "anime"),
    [videos]
  );

  const series = useMemo(() => {
    const items = videos.filter((v) => v.content_type === "series");
    return buildSeriesShows(items).map(showToCatalogItem);
  }, [videos]);

  const animeSeries = useMemo(() => {
    const items = videos.filter((v) => v.content_type === "anime_series");
    return buildSeriesShows(items).map(showToCatalogItem);
  }, [videos]);

  const favorites = useMemo(
    () => videos.filter((v) => favoriteIds.has(v.id) || v.is_favorite),
    [videos, favoriteIds]
  );

  return { movies, animeMovies, series, animeSeries, favorites, error, loading };
}

export default function KidsHomePage({ section = "home" }) {
  const { profile } = useAuth();
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const catalog = useKidsCatalog();
  const { movies, animeMovies, series, animeSeries, favorites, error, loading } = catalog;

  const filteredMovies = useMemo(() => filterByQuery(movies, query), [movies, query]);
  const filteredAnimeMovies = useMemo(
    () => filterByQuery(animeMovies, query),
    [animeMovies, query]
  );
  const filteredSeries = useMemo(() => filterByQuery(series, query), [series, query]);
  const filteredAnimeSeries = useMemo(
    () => filterByQuery(animeSeries, query),
    [animeSeries, query]
  );
  const filteredFavorites = useMemo(
    () => filterByQuery(favorites, query),
    [favorites, query]
  );

  const featuredItems = useMemo(() => {
    const map = new Map();
    const push = (item) => {
      const key = item.catalog_key || item.id;
      if (key == null || map.has(key)) return;
      if (!item.backdrop_url && !item.cover_url && !item.series_backdrop_url) return;
      map.set(key, item);
    };
    [...movies, ...animeMovies, ...series, ...animeSeries].forEach(push);
    return [...map.values()].slice(0, 8);
  }, [movies, animeMovies, series, animeSeries]);

  const pageMeta = {
    home: { heading: "Inicio", sub: "Películas, series y anime para tu edad" },
    movies: { heading: "Películas", sub: "Largometrajes de animación y familia" },
    series: { heading: "Series", sub: "Episodios por temporadas" },
    anime: { heading: "Anime", sub: "Películas y series de anime" },
    favorites: { heading: "Favoritos", sub: "Lo que guardaste" },
  };
  const meta = pageMeta[section] || pageMeta.home;

  function renderStatus() {
    return (
      <>
        {error ? <p className="error">{error}</p> : null}
        {loading ? <p className="kids-loading">Cargando…</p> : null}
      </>
    );
  }

  if (section === "movies") {
    return (
      <KidsShell wide>
        <div className="kids-page">
          <header className="kids-page-head">
            <p className="kids-kicker">Casasm Kids</p>
            <h1>{meta.heading}</h1>
            <p>{meta.sub}</p>
          </header>
          {renderStatus()}
          <KidsGridSection title="Películas" subtitle="Largometrajes" items={filteredMovies} accent="indigo" />
        </div>
      </KidsShell>
    );
  }

  if (section === "series") {
    return (
      <KidsShell wide>
        <div className="kids-page">
          <header className="kids-page-head">
            <p className="kids-kicker">Casasm Kids</p>
            <h1>{meta.heading}</h1>
            <p>{meta.sub}</p>
          </header>
          {renderStatus()}
          <KidsGridSection title="Series" subtitle="Episodios" items={filteredSeries} accent="violet" />
        </div>
      </KidsShell>
    );
  }

  if (section === "anime") {
    return (
      <KidsShell wide>
        <div className="kids-page">
          <header className="kids-page-head">
            <p className="kids-kicker">Casasm Kids</p>
            <h1>{meta.heading}</h1>
            <p>{meta.sub}</p>
          </header>
          {renderStatus()}
          <KidsGridSection
            title="Anime"
            subtitle="Películas"
            items={filteredAnimeMovies}
            accent="orange"
          />
          <KidsGridSection
            title="Anime"
            subtitle="Series"
            items={filteredAnimeSeries}
            accent="fuchsia"
          />
        </div>
      </KidsShell>
    );
  }

  if (section === "favorites") {
    return (
      <KidsShell wide>
        <div className="kids-page">
          <header className="kids-page-head">
            <p className="kids-kicker">Casasm Kids</p>
            <h1>{meta.heading}</h1>
            <p>{meta.sub}</p>
          </header>
          {renderStatus()}
          <KidsGridSection
            title="Tus favoritos"
            items={filteredFavorites}
            emptyText="Aún no tienes favoritos guardados."
            accent="cyan"
          />
        </div>
      </KidsShell>
    );
  }

  return (
    <KidsShell wide>
      {featuredItems.length ? (
        <KidsFeaturedCarousel items={featuredItems} profileName={profile?.name} />
      ) : null}

      <div className="kids-catalog-rows">
        {renderStatus()}

        <KidsGridSection
          items={filteredMovies}
          linkTo="/kids/peliculas"
          accent="indigo"
          hideHead
        />
        <KidsGridSection
          title="Series"
          subtitle="Episodios"
          items={filteredSeries}
          linkTo="/kids/series"
          accent="violet"
        />
        <KidsGridSection
          title="Anime"
          subtitle="Películas"
          items={filteredAnimeMovies}
          linkTo="/kids/anime"
          accent="orange"
        />
        <KidsGridSection
          title="Anime"
          subtitle="Series"
          items={filteredAnimeSeries}
          accent="fuchsia"
        />
        <KidsGridSection
          title="Favoritos"
          items={filteredFavorites}
          linkTo="/kids/favoritos"
          accent="pink"
        />
      </div>
    </KidsShell>
  );
}
